import React, {useState, useCallback, useEffect} from 'react';
import PropTypes from 'prop-types';
import {
  FilteringState,
  IntegratedFiltering,
  IntegratedSorting,
  SelectionState,
  SortingState,
} from '@devexpress/dx-react-grid';
import {useWindowResize} from '@ozgene/gp5.hooks.use-window-resize';
import {
  Grid,
  VirtualTable,
  TableHeaderRow,
  TableSelection,
  TableFilterRow,
} from '@devexpress/dx-react-grid-material-ui';
import {ColumnDefinedFilterCell} from 'util/components/ColumnDefinedFilterCell';
import {CustomRowComponent} from 'util/components/CustomRowComponent';
import {CustomNoDataCell} from 'util/components/CustomNoDataCell';
import {ColumnDefinedCell} from 'util/components/ColumnDefinedCell';
import DateProvider from 'util/components/DateProvider';

function UserList(props) {
  const {
    users,
    onChangeSelection,
    usernameFilterText,
    fullNameFilterText,
    dateFilterText,
    onChangeUsernameFilterText,
    onChangeFullNameFilterText,
    onChangeDateFilterText,
  } = props;
  const [selectedIds, setSelectedIds] = useState([]);
  const [filters, setFilters] = useState([
    usernameFilterText.length > 0 && {
      columnName: 'username',
      operation: 'contains',
      value: usernameFilterText,
    },
    fullNameFilterText.length > 0 && {
      columnName: 'fullName',
      operation: 'contains',
      value: fullNameFilterText,
    },
    dateFilterText.length > 0 && {
      columnName: 'modifiedDt',
      operation: 'contains',
      value: dateFilterText,
    },
  ]);
  const {height} = useWindowResize();
  const handleSelectionChange = useCallback((selectedRows) => {
    const latestSelection = selectedRows[selectedRows.length - 1];

    setSelectedIds([latestSelection]);
  }, []);
  const handleFiltersChange = useCallback(
    (newFilters) => {
      const usernameFilter = newFilters.find(({columnName}) => columnName === 'username');
      const fullNameFilter = newFilters.find(({columnName}) => columnName === 'fullName');
      const dateFilter = newFilters.find(({columnName}) => columnName === 'modifiedDt');
      const usernameFilterValue = usernameFilter?.value ?? '';
      const fullNameFilterValue = fullNameFilter?.value ?? '';
      const dateFilterValue = dateFilter?.value ?? '';

      if (usernameFilterValue !== usernameFilterText) {
        onChangeUsernameFilterText(usernameFilterValue);
      }

      if (fullNameFilterValue !== fullNameFilterText) {
        onChangeFullNameFilterText(fullNameFilterValue);
      }

      if (dateFilterValue !== dateFilterText) {
        onChangeDateFilterText(dateFilterValue);
      }

      setFilters(newFilters);
    },
    [
      dateFilterText,
      fullNameFilterText,
      onChangeDateFilterText,
      onChangeFullNameFilterText,
      onChangeUsernameFilterText,
      usernameFilterText,
    ],
  );

  useEffect(() => {
    setSelectedIds([]);
  }, [users]);

  useEffect(() => {
    const selectedId = selectedIds[0];

    onChangeSelection(users[selectedId]);
  }, [users, onChangeSelection, selectedIds]);
  const userTableColumns = [
    {
      name: 'username',
      title: 'Username',
      getCellValue: (row) => row.username,
    },
    {
      name: 'fullName',
      title: 'Full Name',
      getCellValue: (row) => row.fullName,
    },
    {
      name: 'modifiedDt',
      title: 'Modified Date',
      getCellValue: (row) => row.modifiedDt,
    },
  ];
  const columnExtensions = [
    {
      columnName: 'modifiedDt',
      width: 200,
    },
  ];

  return (
    <Grid rows={users} columns={userTableColumns}>
      <FilteringState filters={filters} onFiltersChange={handleFiltersChange} />
      <SortingState defaultSorting={[{columnName: 'username', direction: 'asc'}]} />
      <SelectionState selection={selectedIds} onSelectionChange={handleSelectionChange} />
      <IntegratedFiltering />
      <IntegratedSorting />
      <DateProvider for={['modifiedDt']} />
      <VirtualTable
        columnExtensions={columnExtensions}
        height={height - 140}
        cellComponent={ColumnDefinedCell}
        noDataCellComponent={CustomNoDataCell}
      />
      <TableHeaderRow showSortingControls />
      <TableSelection
        selectByRowClick
        highlightRow
        showSelectionColumn={false}
        rowComponent={CustomRowComponent}
      />
      <TableFilterRow showFilterSelector cellComponent={ColumnDefinedFilterCell} />
    </Grid>
  );
}

UserList.propTypes = {
  users: PropTypes.array.isRequired,
  onChangeSelection: PropTypes.func.isRequired,
  usernameFilterText: PropTypes.string.isRequired,
  fullNameFilterText: PropTypes.string.isRequired,
  dateFilterText: PropTypes.string.isRequired,
  onChangeUsernameFilterText: PropTypes.func.isRequired,
  onChangeFullNameFilterText: PropTypes.func.isRequired,
  onChangeDateFilterText: PropTypes.func.isRequired,
};

export default UserList;
